// src/utils/authUtils.js
/**
 * Simplified auth utilities for micro-frontends
 * Maintains backward-compatible API while using features-based model
 *
 * NOTE: Backend handles actual authorization - these are for UI visibility only
 */

/**
 * Create auth utilities object for micro-frontends
 * @param {Object} authContext - Contains user and permissions from AuthContext
 */
export const createAuthUtils = (authContext) => {
    const { permissions = {}, user } = authContext;

    // Features array from backend
    const features = permissions.features || [];

    // Legacy: Keep for backward compatibility with MFEs
    const roles = permissions.roles || [];
    const groups = permissions.groups || [];

    // Capabilities = the gateway command input_codes this user may call (derived from
    // ENDPOINT_PERMISSIONS, returned by /profile). null = backend hasn't sent them yet →
    // fail open (show; the gateway still enforces). An array is authoritative.
    const capabilities = permissions.capabilities ?? null;

    /**
     * Check if user is in a privileged group
     * @returns {boolean}
     */
    const isPrivileged = () => {
        const privilegedGroups = ["3d Signals", "dView Admins"];
        return groups.some(group => privilegedGroups.includes(group));
    };

    /**
     * Check if user has a specific permission/role
     * @param {string} permission - Permission to check
     * @returns {boolean}
     */
    const hasPermission = (permission) => {
        if (isPrivileged()) return true;
        return roles.includes(permission);
    };

    /**
     * Check if user has any of the specified permissions
     * @param {string[]} permissionList - List of permissions to check
     * @returns {boolean}
     */
    const hasAnyPermission = (permissionList) => {
        if (!Array.isArray(permissionList) || permissionList.length === 0) return true;
        return permissionList.some(permission => hasPermission(permission));
    };

    /**
     * Check if user is in a specific group
     * @param {string} groupName - Group name to check
     * @returns {boolean}
     */
    const isInGroup = (groupName) => {
        return groups.includes(groupName);
    };

    /**
     * Check if user has a specific feature (NEW - preferred method)
     * @param {string} featureName - Feature to check
     * @returns {boolean}
     */
    const hasFeature = (featureName) => {
        if (isPrivileged()) return true;
        return features.includes(featureName);
    };

    /**
     * Check whether the user may perform an action, keyed by the gateway command
     * input_code (e.g. 'add_unavailability'). Mirrors the gateway's per-command authz —
     * UI visibility only; the gateway still enforces. Single source of truth:
     * ENDPOINT_PERMISSIONS (surfaced via /profile capabilities).
     * @param {string} inputCode
     * @returns {boolean}
     */
    const can = (inputCode) => {
        // No superuser/privileged short-circuit here on purpose: the gateway gives superusers
        // the full capabilities superset (ProfileHandler mirrors handler_base), so can() is
        // purely capability-driven — the client carries no superuser logic.
        if (capabilities == null) return true; // backend not yet emitting caps → fail open
        return capabilities.includes(inputCode);
    };

    /**
     * Get user context info
     * @returns {Object} User context
     */
    const getUserContext = () => ({
        id: user?.userID || user?.id,
        email: user?.userEmail || user?.email,
        firstName: user?.firstName || user?.user_details?.first_name,
        lastName: user?.lastName || user?.user_details?.last_name,
        language: user?.language,
        timezone: user?.userTimezone || user?.timezone
    });

    return {
        // Permission checks (legacy - for backward compatibility)
        hasPermission,
        hasAnyPermission,
        isInGroup,
        isPrivileged,

        // Feature checks (preferred)
        hasFeature,

        // Capability checks (preferred for actions — mirrors the gateway's per-command authz)
        can,

        // User info
        getUserContext,

        // Raw data (for MFEs that need it)
        user,
        permissions,
        features
    };
};